import { forwardRef } from "react";
import { Anchor, LoadingSpinner } from "@newsela/angelou";
import {
  actionPrefixes,
  objectTypes,
  withTrackEvent,
  actionNames,
} from "@newsela/snitch";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import contentTypes from "@/view/constants/contentTypes";
import {
  anchorCss,
  containerCss,
  detailsCss,
  instructionsHeaderCss,
  instructionsTextCss,
  titleCss,
} from "./AssignmentInstructions.style";
import type { Assignment } from "@/types/models/Assignment.interface";

interface AssignmentInstructionsProps {
  assignmentData: Assignment | undefined;
  assignmentsLoading?: boolean;
  currentLevelId?: string;
}

type AnchorProps = React.ComponentProps<typeof Anchor>;

const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;
const AssignmentInstructions = forwardRef<
  HTMLAnchorElement,
  AssignmentInstructionsProps
>(({ assignmentData, assignmentsLoading, currentLevelId }, ref) => {
  if (assignmentsLoading) {
    return <LoadingSpinner />;
  }

  if (!assignmentData) {
    return null;
  }

  const content =
    assignmentData.assignment_members?.[0] &&
    "content" in assignmentData.assignment_members[0]
      ? assignmentData.assignment_members[0].content
      : undefined;

  const assignmentUrl =
    content?.content_type === contentTypes.GENERATION_GENIUS_VIDEO.toLowerCase()
      ? `${MONOLITH_ORIGIN}/view/${content.content_id}/?selectedAssignmentId=${assignmentData.content_id}`
      : `${MONOLITH_ORIGIN}/assignment/${assignmentData.content_id}`;

  const eventProperties = {
    objectType: objectTypes.assignment,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate to an assignment.",
      content_id: currentLevelId,
      object_id: assignmentData?.assignment_id,
    },
  };

  const hasClassrooms = assignmentData.assignment_classrooms?.length > 0;

  return (
    <div css={containerCss}>
      <h3 css={titleCss}>{assignmentData.title}</h3>
      <ul role="list" css={detailsCss}>
        <li>Teacher: {assignmentData.created_by?.user?.display_name}</li>
        {hasClassrooms ? (
          <li>
            Class: {assignmentData.assignment_classrooms?.[0]?.classroom?.name}
          </li>
        ) : null}
      </ul>
      <AnchorWithTrackEvent
        __cssFor={{
          root: anchorCss,
        }}
        href={assignmentUrl}
        ref={ref}
        {...eventProperties}
      >
        View assignment
      </AnchorWithTrackEvent>
      <h4 css={instructionsHeaderCss}>Instructions</h4>
      <p
        css={instructionsTextCss}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: assignmentData.instructions || "",
        }}
      />
    </div>
  );
});

export default AssignmentInstructions;
